import * as React from 'react'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import { SearchableDropdown } from '../ui/SearchableDropdown'
import { SelectedOption } from '../ui/SelectedOption'
import type { TimelineDetails } from './TimelineDetails'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    choices: Flows.DropdownChoice[]
    maxChoices: number
    answerValue: Flows.DropdownAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.DropdownAnswer>
    openOptions?: () => void
}

export class ElementMultiselectDropdown extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    private renderSelectedChoices = (selectedChoices: Flows.Choice[]) => {
        return selectedChoices.map((choice, index) => (
            <SelectedOption
                key={choice.id}
                label={choice.label}
                onRemove={() => this.removeSelectedItem(index, selectedChoices)}
            />
        ))
    }

    private removeSelectedItem = (index: number, selectedChoices: Flows.DropdownChoice[]) => {
        const remainingValues = selectedChoices.map(({ id }) => id)
        remainingValues.splice(index, 1)

        this.props.createAnswer((base) => ({
            ...base,
            type: 'dropdown',
            value: remainingValues,
        }))
    }

    private renderDropdown = (selectedValues: Flows.ChoiceId[], remainingChoices: Flows.DropdownChoice[]) => {
        const { deps, maxChoices, createAnswer } = this.props

        if (maxChoices != null && selectedValues.length === maxChoices) return null

        return (
            <SearchableDropdown
                language={deps.language}
                options={remainingChoices}
                selectedValue={null}
                onChange={(newValue) => {
                    createAnswer((base) => ({
                        ...base,
                        type: 'dropdown',
                        value: [...selectedValues, newValue],
                    }))
                }}
            />
        )
    }

    private tryRender = () => {
        const { deps, elementId, label, timelineDetails, required, choices, validationMessages } = this.props

        const selectedValues = this.props.answerValue ?? []
        const remainingChoices = [...choices]

        // This code isn't very clean but it saves looping over the potentially large list of choices
        // more times than required. This could be performance critical but we haven't timed it
        const selectedChoices = selectedValues.map((value) => {
            let selectedIndex
            const selectedChoice = remainingChoices.find((choice, index) => {
                if (choice.id === value) {
                    // Save a reference to the index so we can remove it from the choices
                    selectedIndex = index
                    return true
                }

                return false
            })

            if (!selectedChoice) throw new Error('Could not find selected choice')
            // Remove the selected item from the remaining choices so a user cannot select it twice
            remainingChoices.splice(selectedIndex, 1)
            return selectedChoice
        })

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={this.props.openOptions}
                />
                <div className="AppCore__flow__ElementMultiselectDropdown">
                    <div className="AppCore__flow__ElementMultiselectDropdown_selectedChoices">
                        {this.renderSelectedChoices(selectedChoices)}
                    </div>
                    {this.renderDropdown(selectedValues, remainingChoices)}
                </div>
            </ExecutionElement>
        )
    }
}
